import { create } from 'zustand';
import type { FilterExpression } from '@/types/query';
import { mergeDrilldown } from '@/lib/merge-drilldown';

/**
 * Active filters and drill-down history. Split out of
 * `dataset-store.ts` during R-8 because the previous combined store
 * mixed three unrelated concerns (registry + filter list + drilldown
 * history) and the `addFilter` body weighed 40+ lines on its own.
 *
 * Lifecycle is now driven by the dataset store: every action there
 * that previously zeroed `state.filters` / `state.drilldownHistory`
 * inline (addDataset, setActiveDataset, removeDataset, clearAll,
 * applyOverrides) now calls {@link useFiltersStore}.getState().reset()
 * after the dataset-store update lands.
 */

type State = {
  /** Active filters applied to all widgets. */
  filters: FilterExpression[];
  /** Drill-down history stack for undo / breadcrumb. */
  drilldownHistory: FilterExpression[][];

  /** Add a filter. Same-column drill-down eq filters merge into 'in'. */
  addFilter: (filter: FilterExpression) => void;
  /** Remove a specific filter by column + value identity. */
  removeFilter: (column: string, value: FilterExpression['value']) => void;
  /** Clear all filters and reset drill-down history. */
  clearFilters: () => void;
  /** Undo the last drill-down step. */
  undoDrilldown: () => void;
  /**
   * Reset to empty state. Used by `dataset-store` when the active
   * dataset changes / is wiped — kept separate from `clearFilters`
   * so callers can distinguish user-driven clears (which should
   * stay observable) from internal lifecycle resets.
   */
  reset: () => void;
};

export const useFiltersStore = create<State>((set) => ({
  filters: [],
  drilldownHistory: [],

  addFilter: (filter) =>
    set((state) => {
      const isDrilldown = filter.source === 'drilldown';
      const nextHistory = isDrilldown
        ? [...state.drilldownHistory, state.filters]
        : state.drilldownHistory;

      if (isDrilldown && filter.operator === 'eq') {
        const existing = state.filters.find(
          (f) => f.column === filter.column && f.source === 'drilldown',
        );
        if (existing) {
          const merged = mergeDrilldown(existing, filter);
          if (!merged) return state; // value already present
          return {
            filters: state.filters.map((f) => (f === existing ? merged : f)),
            drilldownHistory: nextHistory,
          };
        }
      }

      return {
        filters: [...state.filters, filter],
        drilldownHistory: nextHistory,
      };
    }),

  removeFilter: (column, value) =>
    set((state) => {
      const nextFilters = state.filters.filter((f) => {
        if (f.column !== column) return true;
        // Match by serialized value for reliable comparison
        return JSON.stringify(f.value) !== JSON.stringify(value);
      });
      return { filters: nextFilters };
    }),

  clearFilters: () => set({ filters: [], drilldownHistory: [] }),

  undoDrilldown: () =>
    set((state) => {
      if (state.drilldownHistory.length === 0) return state;
      const prev = state.drilldownHistory[state.drilldownHistory.length - 1]!;
      return {
        filters: prev,
        drilldownHistory: state.drilldownHistory.slice(0, -1),
      };
    }),

  reset: () => set({ filters: [], drilldownHistory: [] }),
}));
